import Link from 'next/link';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import PropTypes from 'prop-types';
import React from 'react';
import { Icon } from '@frontend/shared-ui-kit/icon';
import classNames from 'classnames';
import { dash } from 'radash';

const RoundedTab = (props: any) => {
  const {
    name,
    path,
    icon,
    onSetTab,
    selected,
    subLabelComponent,
    url,
    iconSide,
    iconClassName,
    queryParams,
    tabInit,
  } = props;

  const iconComponent = icon ? (
    <Icon size={21} iconName={icon} className={iconClassName} />
  ) : null;

  const tabClick = (name = '') => {
    if (onSetTab) onSetTab(name);
    if (tabInit) tabInit();
  };

  return (
    <div
      onClick={() => tabClick(name)}
      className={classNames(
        'tab flex items-center mr-4 font-bold border-l border-r border-solid border-gray-200 rounded-t-lg',
        { 'bg-gray-100 border-t hover:bg-opacity-50': !selected },

        {
          'relative top-px bg-white text-deep-space-500 z-10 border-t-violet-300 ':
            selected,
        },
      )}
    >
      <div
        className={classNames('py-2 rounded-t-lg', {
          'border-t-4 border-t-solid border-violet-300 h-full': selected,
        })}
      >
        <Link
          href={{
            pathname: `${url}/${path}`,
            query: queryParams,
          }}
          data-cy="tab"
          className={classNames(
            'link flex items-center text-deep-space-500 hover:no-underline px-11 py-3',
            iconSide === 'left' ? 'flex-row' : 'flex-row-reverse',
            selected ? 'text-deep-space-500' : 'text-deep-space-100',
          )}
        >
          {icon ? (
            <span
              className={classNames(
                { 'mr-2.5': iconSide === 'left' },
                { 'ml-2.5': iconSide === 'right' },
              )}
            >
              {iconComponent}
            </span>
          ) : null}
          <span className="label break-normal font-sans	">{name}</span>
          {subLabelComponent && (
            <span className="sub-label ml-3 font-sans">
              {subLabelComponent}
            </span>
          )}
        </Link>
      </div>
    </div>
  );
};

const BlockTab = (props: any) => {
  const {
    name,
    path,
    icon,
    onSetTab,
    selected,
    subLabelComponent,
    url,
    iconSide,
    queryParams,
    tabInit,
  } = props;

  const iconComponent = icon ? <Icon size={21} iconName={icon} /> : null;

  const tabClick = (name = '') => {
    if (onSetTab) onSetTab(name);
    if (tabInit) tabInit();
  };

  return (
    <div
      onClick={() => tabClick(name)}
      className={classNames('flex items-center decoration-none mr-4 relative')}
    >
      <Link
        href={{
          pathname: `${url}/${path}`,
          query: queryParams,
        }}
        data-cy={'tab-' + dash(name)}
        className={classNames(
          `flex items-center justify-center focus:outline-none no-underline center px-2 text-sm sm:text-base border-b-4 border-transparent hover:border-violet-300 focus:border-b-4 focus:border-violet-300 ${
            iconSide === 'left' ? 'flex-row' : 'flex-row-reverse'
          }`,

          { 'border-violet-300 border-b-4 font-bold': selected },
        )}
      >
        {icon ? (
          <span
            className={classNames(
              { 'mr-2.5': iconSide === 'left' },
              { 'ml-2.5': iconSide === 'right' },
            )}
          >
            {iconComponent}
          </span>
        ) : null}
        <span className="break-normal	whitespace-nowrap font-sans">{name}</span>
        {subLabelComponent && (
          <span className="ml-3 font-sans">{subLabelComponent}</span>
        )}
      </Link>
    </div>
  );
};

//	Tab takes in all props that react-router's Route Component would
const Tab = (props: any) => {
  const { styleType } = props;

  return styleType === 'round' ? (
    <RoundedTab {...props} />
  ) : (
    <BlockTab {...props} />
  );
};

Tab.propTypes = {
  name: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.string,
  isShowing: PropTypes.bool,
  children: PropTypes.node,
  iconSide: PropTypes.oneOf(['left', 'right']),
  onSetTab: PropTypes.func,
  selected: PropTypes.bool,
  subLabelComponent: PropTypes.node,
  absolutePath: PropTypes.string,
  iconClassName: PropTypes.string,
  tabInit: PropTypes.func,
  queryParams: PropTypes.string,
};

Tab.defaultProps = {
  subLabelComponent: null,
  onSetTab: null,
  selected: false,
  icon: null,
  iconSide: 'left',
  tabInit: null,
};

export default Tab;
